import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Monitor, Users } from "lucide-react";
import {
  AppLink,
  Button,
  Callout,
  EmptyState,
  IconButton,
  SegmentedFilter,
  Skeleton,
  formatTime,
  formatCount,
} from "@amber/ui";
import type { HomeFrame } from "@amber/application/contracts";
import { CoveragePanel } from "./CoveragePanel";
import { DeliveryPanel } from "./DeliveryPanel";
import { FRAME_OPTIONS } from "./format";
import { FunnelPanel } from "./FunnelPanel";
import { PeoplePanel } from "./PeoplePanel";
import { PlatformsPanel } from "./PlatformsPanel";
import { homeQuery } from "./queries";
import { StatusPanel } from "./StatusPanel";
import { SustainabilityPanel } from "./SustainabilityPanel";
import { TeamScreen } from "./TeamScreen";

export function HomeTopbar({
  frame,
  team,
  onFrame,
  onTeam,
}: {
  frame: HomeFrame;
  team: boolean;
  onFrame: (frame: HomeFrame) => void;
  onTeam: () => void;
}) {
  const query = useQuery(homeQuery(frame, team));
  if (!query.data || query.data.day_one) return null;
  return (
    <>
      <div className="flex flex-none items-center gap-1">
        <span className="text-md text-muted max-[1279px]:hidden">Last</span>
        <SegmentedFilter label="Time frame" value={frame} options={FRAME_OPTIONS} onChange={onFrame} />
      </div>
      <IconButton label="Team screen (Shift+T)" onClick={onTeam} className="max-[900px]:hidden">
        <Monitor size={15} strokeWidth={1.8} aria-hidden="true" />
      </IconButton>
    </>
  );
}

export function HomeStatus({ frame, team }: { frame: HomeFrame; team: boolean }) {
  const query = useQuery(homeQuery(frame, team));
  const d = query.data;
  if (!d || d.day_one) return null;
  return (
    <span className="max-narrow:hidden">
      Updated {formatTime(d.generated_at)} ·{" "}
      <b className="font-strong text-fg">{formatCount(d.activity.in_progress)}</b> in progress ·{" "}
      <b className="font-strong text-fg">{formatCount(d.activity.waiting)}</b> waiting ·{" "}
      <b className="font-strong text-fg">{formatCount(d.coverage.open_gaps.count)}</b> open gaps ·{" "}
      <kbd className="font-mono">Shift+T</kbd> team screen
    </span>
  );
}

export function HomeView({
  frame,
  onFrame,
  team,
  onTeam,
}: {
  frame: HomeFrame;
  onFrame: (frame: HomeFrame) => void;
  team: boolean;
  onTeam: (team: boolean) => void;
}) {
  const query = useQuery(homeQuery(frame, team));
  const [funnelOpen, setFunnelOpen] = useState(false);
  const data = query.data;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (e.key === "T" && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        onTeam(!team);
      } else if (e.key === "Escape" && team) {
        onTeam(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [team, onTeam]);

  if (query.isError && !data) {
    return (
      <div className="p-4">
        <Callout
          tone="attention"
          role="alert"
          title="Couldn't load the home figures"
          actions={
            <Button size="sm" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          {query.error.message}
        </Callout>
      </div>
    );
  }
  if (!data) {
    return (
      <div aria-busy="true" className="space-y-4 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (data.day_one) {
    return (
      <section aria-labelledby="view-title" className="flex flex-1 flex-col">
        <h1 id="view-title" className="m-0 px-4 pt-3.5 text-2xl font-semibold">
          Home
        </h1>
        <EmptyState icon={<Users />} title="No people are tracked yet">
          Start by adding the people to follow in{" "}
          <AppLink href="/profiles" className="text-link underline">
            Profiles
          </AppLink>
          . Figures appear here once collection is running.
        </EmptyState>
      </section>
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <h1 className="sr-only">Home</h1>
      <div className="mx-auto max-w-[1400px] space-y-4 p-4 max-narrow:p-2">
        <StatusPanel data={data} team={false} />
        <div className="grid grid-cols-[minmax(0,1fr)_350px] gap-4 max-compact:grid-cols-1">
          <DeliveryPanel
            data={data}
            team={false}
            onFunnel={() => {
              setFunnelOpen(true);
            }}
          />
          <SustainabilityPanel data={data} team={false} />
          <PlatformsPanel data={data} team={false} />
          <CoveragePanel data={data} />
        </div>
        <PeoplePanel data={data} />
      </div>
      {funnelOpen ? (
        <FunnelPanel
          frame={frame}
          onFrame={onFrame}
          onClose={() => {
            setFunnelOpen(false);
          }}
        />
      ) : null}
      {team ? (
        <TeamScreen
          data={data}
          onExit={() => {
            onTeam(false);
          }}
        />
      ) : null}
    </div>
  );
}
